import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Trophy, LogOut, Flag, Dumbbell, Smartphone, UserRound } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useUnit } from "../context/UnitContext";
import { ExerciseCard } from "../components/ExerciseCard";
import { RestTimer } from "../components/RestTimer";
import { SessionRecap } from "../components/SessionRecap";
import { SessionJournal } from "../components/SessionJournal";

const JOURS = [
  { id: "Jour 1", label: "J1 · Pecs/Dos" },
  { id: "Jour 2", label: "J2 · Jambes" },
  { id: "Jour 3", label: "J3 · Bras/Épaules" },
  { id: "Jour 4", label: "J4 · Repos actif" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function WorkoutPage() {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const { unit, toggleUnit } = useUnit();
  const [showRecap, setShowRecap] = useState(false);
  const [bloc, setBloc] = useState(localStorage.getItem("gym_bloc") || "A");
  const [jour, setJour] = useState(localStorage.getItem("gym_jour") || "Jour 1");
  const [program, setProgram] = useState([]);
  const [sets, setSets] = useState([]);
  const [timer, setTimer] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [prev, setPrev] = useState({});
  const [blocs, setBlocs] = useState([{ bloc: "A" }, { bloc: "B" }]);
  const [online, setOnline] = useState(navigator.onLine);
  const [loading, setLoading] = useState(true);
  const syncingRef = useRef(false);

  const syncPending = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    try {
      const q = JSON.parse(localStorage.getItem("gym_pending_sets") || "[]");
      if (q.length === 0) return;
      localStorage.setItem("gym_pending_sets", "[]");
      const remaining = [];
      let synced = 0;
      for (const p of q) {
        try {
          await api.post("/sets", p);
          synced++;
        } catch (e) {
          remaining.push(p);
        }
      }
      if (remaining.length > 0) {
        const cur = JSON.parse(localStorage.getItem("gym_pending_sets") || "[]");
        localStorage.setItem("gym_pending_sets", JSON.stringify([...remaining, ...cur]));
      }
      if (synced > 0) {
        toast.success(`${synced} série${synced > 1 ? "s" : ""} hors ligne synchronisée${synced > 1 ? "s" : ""} !`);
        api.get("/sets", { params: { date: today(), bloc, jour } }).then(({ data }) => setSets(data)).catch(() => {});
      }
    } finally {
      syncingRef.current = false;
    }
  }, [bloc, jour]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); syncPending(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    syncPending();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [syncPending]);

  useEffect(() => {
    api.get("/blocs").then(({ data }) => { if (data.length > 0) setBlocs(data); }).catch(() => {});
    api.get("/cycle").then(({ data }) => {
      setCycle(data);
      setBloc(data.bloc);
      setJour(data.jour);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("gym_bloc", bloc);
    localStorage.setItem("gym_jour", jour);
    setLoading(true);
    api.get("/program", { params: { bloc, jour } }).then(({ data }) => {
      setProgram(data);
      localStorage.setItem(`gym_cache_program_${bloc}_${jour}`, JSON.stringify(data));
    }).catch(() => {
      const c = localStorage.getItem(`gym_cache_program_${bloc}_${jour}`);
      if (c) setProgram(JSON.parse(c));
    }).finally(() => setLoading(false));
    api.get("/sets", { params: { date: today(), bloc, jour } }).then(({ data }) => setSets(data)).catch(() => {
      const q = JSON.parse(localStorage.getItem("gym_pending_sets") || "[]");
      setSets(q.filter((p) => p.date === today() && p.bloc === bloc && p.jour === jour)
        .map((p, i) => ({ ...p, id: `local-${i}`, pending: true, prs: [], volume: (p.poids || 0) * p.reps })));
    });
    api.get("/previous", { params: { bloc, jour, before: today() } }).then(({ data }) => setPrev(data)).catch(() => {});
  }, [bloc, jour]);

  const validate = async (ex, num, vals) => {
    const payload = {
      date: today(), bloc, jour, groupe: ex.groupe, exercice: ex.exercice,
      muscle: ex.muscle, serie_num: num, ...vals,
    };
    try {
      const { data } = await api.post("/sets", payload);
      setSets((prev) => [...prev, data.set]);
      if (data.prs.length > 0) {
        toast.success(`NOUVEAU RECORD — ${ex.exercice}`, {
          description: data.pr_labels.join(" · "),
          icon: <Trophy size={18} className="text-amber-400" />,
          duration: 5000,
        });
      } else {
        toast.success("Série validée");
      }
      if (ex.repos_s > 0) setTimer({ key: Date.now(), seconds: ex.repos_s });
    } catch (e) {
      if (!e.response) {
        const q = JSON.parse(localStorage.getItem("gym_pending_sets") || "[]");
        q.push(payload);
        localStorage.setItem("gym_pending_sets", JSON.stringify(q));
        setSets((prev) => [...prev, { ...payload, id: `local-${Date.now()}`, pending: true, prs: [],
          volume: (payload.poids || 0) * payload.reps, created_at: new Date().toISOString() }]);
        setOnline(false);
        toast.info("Hors ligne — série enregistrée localement, synchro automatique au retour du réseau");
        if (ex.repos_s > 0) setTimer({ key: Date.now(), seconds: ex.repos_s });
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    }
  };

  const closeTimer = useCallback(() => setTimer(null), []);

  const groups = [];
  program.forEach((ex) => {
    const last = groups[groups.length - 1];
    if (last && last.name === ex.groupe) last.items.push(ex);
    else groups.push({ name: ex.groupe, type: ex.type, items: [ex] });
  });

  return (
    <div className="pb-32">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/85 border-b border-[#2E2E3A] px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-4xl text-white leading-none flex items-center gap-2">
              <Dumbbell size={28} className="text-[#FF3B30]" /> ENTRAÎNEMENT
            </h1>
            <p className="text-[#FFE600] font-bold text-xs mt-0.5">Salut {user.name} · RIR 1-2 · pas d'échec sur le lourd</p>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="unit-toggle-button" onClick={toggleUnit}
              className="text-xs font-mono border border-white/15 px-2 py-1.5 text-[#ccc] hover:text-white hover:border-[#FF3B30] transition-colors uppercase">
              {unit}
            </button>
            <button data-testid="install-guide-button" onClick={() => window.dispatchEvent(new Event("open-install-guide"))}
              className="text-[#B0B0B4] hover:text-white p-2 transition-colors" aria-label="Guide d'installation">
              <Smartphone size={18} />
            </button>
            <button data-testid="profile-button" onClick={() => navigate("/profil")}
              className="text-[#B0B0B4] hover:text-white p-2 transition-colors" aria-label="Mon profil">
              <UserRound size={18} />
            </button>
            <button data-testid="logout-button" onClick={logoutUser} className="text-[#B0B0B4] hover:text-white p-2 transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {blocs.map((b) => (
            <button key={b.bloc} data-testid={`bloc-${b.bloc}-button`} onClick={() => setBloc(b.bloc)}
              className={`h-10 px-5 font-display text-xl tracking-wide whitespace-nowrap transition-colors rounded-[8px] ${
                bloc === b.bloc ? "bg-[#00E5FF] text-black shadow-[0_0_16px_rgba(0,229,255,0.6)]" : "bg-[#1A1A22] text-white border-2 border-[#3A3A45]"}`}>
              BLOC {b.bloc}{b.label ? ` · ${b.label.toUpperCase()}` : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {JOURS.map((j) => (
            <button key={j.id} data-testid={`jour-${j.id.slice(-1)}-button`} onClick={() => setJour(j.id)}
              className={`h-9 px-3 text-xs font-bold whitespace-nowrap border-2 transition-colors rounded-[8px] ${
                jour === j.id ? "border-[#FF2D95] text-[#FF2D95] shadow-[0_0_14px_rgba(255,45,149,0.55)] bg-black" : "bg-[#1A1A22] border-[#3A3A45] text-white"}`}>
              {j.label}
            </button>
          ))}
        </div>
        {!online && (
          <p data-testid="offline-banner" className="text-[11px] mt-2 px-2 py-1.5 bg-amber-500/10 border border-amber-500/40 text-amber-400">
            Mode hors ligne — tes séries sont sauvegardées sur le téléphone et seront synchronisées automatiquement
          </p>
        )}
        {cycle && (
          <p data-testid="cycle-banner" className="text-xs mt-2">
            <span className="text-[#FF3B30] font-semibold uppercase tracking-wider">Cycle {cycle.cycle}</span>
            <span className="text-[#B0B0B4]"> · Prochaine séance suggérée : Bloc {cycle.bloc} · {cycle.jour}</span>
          </p>
        )}
      </header>

      <main className="px-4 mt-4 space-y-6">
        {loading && program.length === 0 && (
          <div data-testid="workout-loading" className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="h-6 w-40 bg-[#14141A] animate-pulse" />
                <div className="bg-[#14141A] border border-[#2E2E3A] p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-[#14141A] animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-3/4 bg-[#14141A] animate-pulse" />
                      <div className="h-3 w-1/2 bg-[#14141A] animate-pulse" />
                    </div>
                  </div>
                  {[0, 1, 2].map((j) => <div key={j} className="h-10 bg-[#14141A]/60 animate-pulse" />)}
                </div>
              </div>
            ))}
          </div>
        )}
        {groups.map((g) => (
          <section key={g.name}>
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-[#00E5FF] text-[17px] font-black uppercase tracking-[2px]">{g.name.toUpperCase()}</h2>
              {(g.type === "Superset" || g.type === "Giant set") && (
                <span className="text-[10px] uppercase tracking-widest text-[#C8C8D0]">sans repos entre les exos</span>
              )}
            </div>
            <div className={`space-y-px ${g.items.length > 1 ? "border-l-2 border-[#00E5FF]/60 pl-2" : ""}`}>
              {g.items.map((ex) => (
                <ExerciseCard key={ex.exercice} ex={ex} prev={prev[ex.exercice]}
                  loggedSets={sets.filter((s) => s.exercice === ex.exercice).sort((a, b) => a.serie_num - b.serie_num)}
                  onValidate={validate} />
              ))}
            </div>
          </section>
        ))}
        {!loading && <SessionJournal date={today()} bloc={bloc} jour={jour} />}
        {sets.length > 0 && (
          <button data-testid="finish-session-button" onClick={() => setShowRecap(true)}
            className="w-full h-12 bg-[#FF3B30] text-white font-display text-2xl tracking-wide hover:bg-[#e0322a] active:scale-[0.99] transition-all flex items-center justify-center gap-2">
            <Flag size={18} /> TERMINER LA SÉANCE
          </button>
        )}
      </main>

      {showRecap && <SessionRecap sets={sets} bloc={bloc} jour={jour} onClose={() => setShowRecap(false)} />}
      {timer && <RestTimer key={timer.key} seconds={timer.seconds} onDone={closeTimer} />}
    </div>
  );
}
